import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Calculator, BookOpen, FlaskConical, Sparkles, ArrowLeft, Globe, Landmark, CircleHelp, Volume2, Palette, Heart, Wallet, Users, Brain, Target, Lightbulb, TrendingUp } from 'lucide-react';
import { useGame } from '../context/GameContext';

const SubjectSelection = () => {
  const navigate = useNavigate();
  const { setSelectedSubject, selectedAge, playerName } = useGame();
  const [hoveredSubject, setHoveredSubject] = useState(null);
  const [playingSound, setPlayingSound] = useState(null);

  // Cancel any speech when component unmounts
  useEffect(() => {
    return () => {
      window.speechSynthesis.cancel();
    };
  }, []);

  const playSubjectSound = (text) => {
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.rate = 0.9;
    utterance.pitch = 1.1;
    utterance.volume = 0.8;
    window.speechSynthesis.speak(utterance);
  };

  // Subjects for Young Scholar (6-8 years)
  const elementarySubjects = [
    {
      id: 'math',
      label: 'Math Magic',
      icon: Calculator,
      color: 'from-orange-400 to-red-400',
      bgColor: '#F97316',
      image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=300&fit=crop',
      emoji: '🔢',
      description: 'Numbers, counting, and problem solving!',
      sound: 'Math is fun! Let\'s solve puzzles together!',
      funFact: 'Math is used in video games! 🎮'
    },
    {
      id: 'language',
      label: 'Word Wizard',
      icon: BookOpen,
      color: 'from-purple-400 to-pink-400',
      bgColor: '#A855F7',
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400&h=300&fit=crop',
      emoji: '📚',
      description: 'Letters, reading, and vocabulary!',
      sound: 'Words are powerful! Let\'s learn new words!',
      funFact: 'There are over 170,000 words in English! 📖'
    },
    {
      id: 'science',
      label: 'Science Explorer',
      icon: FlaskConical,
      color: 'from-cyan-400 to-blue-400',
      bgColor: '#06B6D4',
      image: 'https://images.unsplash.com/photo-1567306226416-28f0efdc88ce?w=400&h=300&fit=crop',
      emoji: '🔬',
      description: 'Animals, nature, and discoveries!',
      sound: 'Science is amazing! Let\'s discover how things work!',
      funFact: 'Water is the only substance that exists as solid, liquid, and gas! 💧'
    },
    {
      id: 'geography',
      label: 'World Geography',
      icon: Globe,
      color: 'from-emerald-400 to-teal-500',
      bgColor: '#10B981',
      image: 'https://images.unsplash.com/photo-1630976263319-84ef8df5f8a4?w=400&h=300&fit=crop',
      emoji: '🌍',
      description: 'Explore countries and continents!',
      sound: 'Let\'s travel the world! There are 7 continents to explore!',
      funFact: 'The Pacific Ocean is bigger than all the land on Earth! 🌊'
    },
    {
      id: 'history',
      label: 'History Heroes',
      icon: Landmark,
      color: 'from-amber-400 to-yellow-500',
      bgColor: '#F59E0B',
      image: 'https://images.unsplash.com/photo-1657783950449-0079038561db?w=400&h=300&fit=crop',
      emoji: '🏰',
      description: 'Discover the past and famous people!',
      sound: 'History is like a time machine! Let\'s learn about the past!',
      funFact: 'Ancient Egyptians built pyramids over 4,500 years ago! 🔺'
    },
    {
      id: 'true-false',
      label: 'True or False',
      icon: CircleHelp,
      color: 'from-rose-400 to-red-500',
      bgColor: '#F43F5E',
      image: 'https://images.pexels.com/photos/32327241/pexels-photo-32327241.jpeg?w=400&h=300&fit=crop',
      emoji: '❓',
      description: 'Fun quiz challenges!',
      sound: 'True or False? Test your knowledge with fun questions!',
      funFact: 'Your brain can hold about 2.5 million gigabytes of info! 🧠'
    },
    {
      id: 'art-creativity',
      label: 'Art & Creativity',
      icon: Palette,
      color: 'from-pink-400 to-fuchsia-500',
      bgColor: '#EC4899',
      image: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=400&h=300&fit=crop',
      emoji: '🎨',
      description: 'Creative challenges and art appreciation!',
      sound: 'Let\'s get creative! Art is everywhere around us!',
      funFact: 'The Mona Lisa has no eyebrows! 🖼️'
    },
    {
      id: 'health-wellness',
      label: 'Health & Wellness',
      icon: Heart,
      color: 'from-red-400 to-pink-500',
      bgColor: '#EF4444',
      image: 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400&h=300&fit=crop',
      emoji: '💪',
      description: 'Nutrition basics and healthy habits!',
      sound: 'Stay healthy and strong! Good habits make you feel great!',
      funFact: 'Your heart beats about 100,000 times a day! ❤️'
    },
    {
      id: 'mixed',
      label: 'Mixed Adventure',
      icon: Sparkles,
      color: 'from-violet-400 to-purple-500',
      bgColor: '#8B5CF6',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop',
      emoji: '✨',
      description: 'A bit of everything - surprise me!',
      sound: 'Get ready for a surprise mix of fun learning!',
      funFact: 'Learning new things makes your brain stronger! 💪'
    }
  ];

  // Subjects for Smart Champion (9-12 years)
  const advancedSubjects = [
    {
      id: 'math',
      label: 'Advanced Math',
      icon: Calculator,
      color: 'from-orange-400 to-red-400',
      bgColor: '#F97316',
      image: 'https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400&h=300&fit=crop',
      emoji: '🧮',
      description: 'Algebra, geometry, and problem solving!',
      sound: 'Advanced math! Let\'s tackle challenging problems!',
      funFact: 'The equals sign was invented in 1557! ='
    },
    {
      id: 'language',
      label: 'Language Arts',
      icon: BookOpen,
      color: 'from-purple-400 to-pink-400',
      bgColor: '#A855F7',
      image: 'https://images.unsplash.com/photo-1456513080510-7bf3a84b82f8?w=400&h=300&fit=crop',
      emoji: '📝',
      description: 'Writing, grammar, and literature!',
      sound: 'Master the art of language and writing!',
      funFact: 'Shakespeare invented over 1,700 words! 📖'
    },
    {
      id: 'science',
      label: 'Science Lab',
      icon: FlaskConical,
      color: 'from-cyan-400 to-blue-400',
      bgColor: '#06B6D4',
      image: 'https://images.unsplash.com/photo-1567306226416-28f0efdc88ce?w=400&h=300&fit=crop',
      emoji: '🔬',
      description: 'Biology, chemistry, and physics!',
      sound: 'Explore the wonders of science!',
      funFact: 'Lightning strikes Earth about 100 times per second! ⚡'
    },
    {
      id: 'geography',
      label: 'World Explorer',
      icon: Globe,
      color: 'from-emerald-400 to-teal-500',
      bgColor: '#10B981',
      image: 'https://images.unsplash.com/photo-1630976263319-84ef8df5f8a4?w=400&h=300&fit=crop',
      emoji: '🗺️',
      description: 'Countries, cultures, and geography!',
      sound: 'Explore the world! Learn about different cultures!',
      funFact: 'Russia spans 11 time zones! 🌍'
    },
    {
      id: 'history',
      label: 'History Quest',
      icon: Landmark,
      color: 'from-amber-400 to-yellow-500',
      bgColor: '#F59E0B',
      image: 'https://images.unsplash.com/photo-1657783950449-0079038561db?w=400&h=300&fit=crop',
      emoji: '📜',
      description: 'World history and civilizations!',
      sound: 'Journey through time and discover history!',
      funFact: 'Cleopatra lived closer to the iPhone than to the pyramids! 🏛️'
    },
    {
      id: 'financial-literacy',
      label: 'Financial Literacy',
      icon: Wallet,
      color: 'from-green-400 to-emerald-500',
      bgColor: '#22C55E',
      image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=400&h=300&fit=crop',
      emoji: '💰',
      description: 'Saving, budgeting, and smart money!',
      sound: 'Learn about money! Saving, spending, and being smart with cash!',
      funFact: 'The first coins were made over 2,600 years ago! 🪙'
    },
    {
      id: 'social-emotional',
      label: 'Social-Emotional',
      icon: Users,
      color: 'from-sky-400 to-blue-500',
      bgColor: '#0EA5E9',
      image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=400&h=300&fit=crop',
      emoji: '🤝',
      description: 'Empathy, teamwork, and leadership!',
      sound: 'Build amazing relationships! Learn about empathy and teamwork!',
      funFact: 'Smiling can actually make you feel happier! 😊'
    },
    {
      id: 'critical-thinking',
      label: 'Critical Thinking',
      icon: Brain,
      color: 'from-indigo-400 to-purple-500',
      bgColor: '#6366F1',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop',
      emoji: '🧠',
      description: 'Logic, reasoning, and problem solving!',
      sound: 'Think critically! Analyze and solve complex problems!',
      funFact: 'Your brain uses 20% of your body\'s energy! ⚡'
    },
    {
      id: 'mixed',
      label: 'Challenge Mix',
      icon: Sparkles,
      color: 'from-violet-400 to-purple-500',
      bgColor: '#8B5CF6',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=400&h=300&fit=crop',
      emoji: '🌟',
      description: 'Mixed challenges from all subjects!',
      sound: 'Ready for anything! A mix of all subjects!',
      funFact: 'Your brain creates new neural pathways when learning! 🧠'
    }
  ];

  // Select subjects based on age group
  const subjects = selectedAge === 'advanced' ? advancedSubjects : elementarySubjects;
  const pageTitle = selectedAge === 'advanced' ? 'Smart Champion Subjects' : 'Young Scholar Subjects';
  const pageEmoji = selectedAge === 'advanced' ? '🏆' : '🎓';

  const handleSelect = (subject) => {
    setPlayingSound(subject.id);
    playSubjectSound(subject.sound);
    
    setTimeout(() => {
      setSelectedSubject(subject.id);
      navigate('/game-format');
    }, 1500);
  };

  return (
    <div className="min-h-screen p-4 md:p-8" style={{
      background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 50%, #7dd3fc 100%)'
    }}>
      <div className="max-w-7xl mx-auto">
        <Button
          onClick={() => navigate('/age-selection')}
          variant="ghost"
          className="mb-6 text-lg font-semibold hover:scale-110 transition-transform"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back
        </Button>

        <div className="text-center mb-8 md:mb-12">
          <div className="text-6xl md:text-8xl mb-4 animate-bounce">{pageEmoji}</div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-4" style={{
            fontFamily: '"Comic Neue", "Fredoka", cursive, sans-serif'
          }}>
            Hey {playerName}! Pick Your Subject!
          </h1>
          <p className="text-xl md:text-2xl lg:text-3xl text-gray-700 font-bold">
            {selectedAge === 'advanced' ? 'Challenge yourself with advanced topics! 🚀' : 'What do you want to explore today? 🚀'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
          {subjects.map((subject, index) => {
            const IconComponent = subject.icon;
            const isHovered = hoveredSubject === subject.id;
            const isPlaying = playingSound === subject.id;
            return (
              <Card
                key={subject.id}
                className={`cursor-pointer transition-all duration-300 overflow-hidden ${isPlaying ? 'scale-105' : 'hover:scale-105'} hover:shadow-2xl`}
                style={{
                  animationDelay: `${index * 0.1}s`,
                  borderRadius: '24px',
                  border: isHovered ? '4px solid #FFD700' : '3px solid transparent',
                  boxShadow: isHovered ? '0 20px 40px rgba(0,0,0,0.15)' : '0 4px 6px rgba(0,0,0,0.1)'
                }}
                onClick={() => handleSelect(subject)}
                onMouseEnter={() => setHoveredSubject(subject.id)}
                onMouseLeave={() => setHoveredSubject(null)}
              >
                {/* Image Section */}
                <div 
                  className="h-36 md:h-40 relative overflow-hidden"
                  style={{ backgroundColor: subject.bgColor }}
                >
                  <img 
                    src={subject.image} 
                    alt={subject.label}
                    className={`w-full h-full object-cover transition-transform duration-500 ${isHovered ? 'scale-110' : 'scale-100'}`}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                  {/* Gradient Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                  
                  {/* Emoji Badge */}
                  <div className={`absolute top-3 right-3 text-3xl md:text-4xl ${isHovered || isPlaying ? 'animate-bounce' : ''}`}>
                    {subject.emoji}
                  </div>
                  
                  {/* Sound indicator when playing */}
                  {isPlaying && (
                    <div className="absolute top-3 left-3 bg-white/90 rounded-full p-2 animate-pulse">
                      <Volume2 className="w-4 h-4 text-blue-500" />
                    </div>
                  )}
                  
                  {/* Icon Badge */}
                  <div className="absolute bottom-3 left-3 bg-white/90 rounded-full p-2 shadow-lg">
                    <IconComponent className="w-5 h-5" style={{ color: subject.bgColor }} />
                  </div>
                </div>
                
                {/* Content Section */}
                <div className="p-4 bg-white">
                  <h3 className="text-lg md:text-xl font-bold text-gray-800 mb-1 text-center" style={{
                    fontFamily: '"Fredoka", "Comic Neue", cursive, sans-serif'
                  }}>
                    {subject.label}
                  </h3>
                  <p className="text-xs md:text-sm text-gray-600 text-center mb-3">{subject.description}</p>
                  
                  {/* Fun Fact - shows on hover */}
                  {isHovered && (
                    <p className="text-xs text-amber-600 text-center mb-3 font-medium animate-pulse">
                      💡 {subject.funFact}
                    </p>
                  )}
                  
                  <div
                    className={`w-full py-2.5 text-sm md:text-base font-bold text-center text-white transition-all duration-300 rounded-xl ${isPlaying ? 'animate-pulse' : ''}`}
                    style={{
                      background: `linear-gradient(135deg, ${subject.bgColor}, ${subject.bgColor}dd)`,
                      fontFamily: '"Fredoka", cursive'
                    }}
                  >
                    {isPlaying ? '🔊 Loading...' : "Let's Learn! 📚"}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Fun decorative elements */}
      </div>
    </div>
  );
};

export default SubjectSelection;
